import React, {useState} from 'react';
import { useUserAndGameContext } from '../../../contexts/UserAndGameContext';
import {Box, Typography} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { DateTime } from 'luxon';
import { ReactComponent as Ellipses } from '../../../assets/images/ellipsis-solid.svg';
import ShareModal from './ShareModal';
import ContentModal from './ContentModal';
import useJoinGameClick from '../../../hooks/general/useJoinGameClick';

const TemplatedSnackbarWithMenu = () => {
    const {
        gameData,
        myUserId,
        setJoinOpen,
        isUserInThisPool,
        maxEntriesThisPool,
        howManyEntriesInThisPool,
        hasJoinDeadlinePassed,
        page,
        masterPoolData,
    } = useUserAndGameContext();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [menu, setMenu] = useState(false);
    const [activeModal, setActiveModal] = useState(null);

    const joinGameClick = useJoinGameClick();
    //console.log("Active Modal: ", activeModal, "Menu State: ", menu);


    // Get snackbar info from game data or use defaults
    const snackBarInfo = gameData?.gameSnackbarInfo;

    // If snackBarInfo is missing or empty, return null (don't render anything)
    if (!snackBarInfo || Object.keys(snackBarInfo).length === 0) {
        return null;
    }

    const {backgroundColor, textColor, textLineOne, textLineTwo, expirationDate, menuItems, heroImages } = snackBarInfo;

    const homePageHeroInfo = heroImages?.homePage;
    const otherPagesHeroInfo = heroImages?.otherPages;

    // Check if the snackBar has expired
    if (expirationDate) {
        const currentDate = DateTime.now().setZone('America/New_York');
        const expireDate = DateTime.fromISO(expirationDate, { zone: 'America/New_York' });

        if (currentDate > expireDate) {
        return null;
        }
    }

    const SubMenu = ({ menuItems }) => {
        return (
        <Box sx={{
            background:'#fff',
            color:'#373737',
            fontWeight:'400',
            position:'absolute',
            zIndex:7,
            top:isMobile? '163px': '215px',
            right: 0,
            left: 'auto',
            width:'138px',
            height:'auto',
            textAlign:'left',
            lineHeight:'1.4',
            paddingTop:'16px',
            paddingLeft: '10px',
            boxShadow:'0 0 15px #000'
        }}>
            {menuItems?.map((item, index) => (
            <Box
                key={index}
                sx={{ marginBottom: '16px', cursor: 'pointer' }}
                onClick={() => {
                setActiveModal(item.modal);
                setMenu(false);
                }}
            >
                <Typography sx={{ fontSize: '12px' }}>{item.text}</Typography>
            </Box>
            ))}
        </Box>
        );
    };

    // Define common box styles
    const boxStyles = {
        top: isMobile ? 80 : 136,
        left: 0,
        zIndex: 8,
        backgroundColor: backgroundColor,
        width: '100%',
        padding: '16px',
        paddingTop: '22px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: textColor,
        fontSize: isMobile ? '12px' : '16px',
        fontWeight: 700,
        textAlign:'left'
    };

    // Determine which button to show
    const renderActionButton = () => {
        if (masterPoolData?.gameParameters?.notJoinable) {
            // If the game is not joinable, return null
            return null;
        }
        if (!isUserInThisPool && !hasJoinDeadlinePassed) {
            // User not in pool, deadline not passed - Join Pool +
            return (
                <Box
                    onClick={() => joinGameClick()}
                    sx={{
                        backgroundColor: '#FFC60A',
                        borderColor: textColor,
                        borderRadius: '4px',
                        padding: '4px 8px',
                        height: '32px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 700,
                        color: '#1A1C1E',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        '&:hover': {
                            backgroundColor: 'rgba(255,198,10,0.8)',
                        }
                    }}
                >
                    Join Pool +
                </Box>
            );
        }

        if (!isUserInThisPool && hasJoinDeadlinePassed) {
            // User not in pool, deadline passed - Pool Closed X
            return (
                <Box sx={{
                    backgroundColor: '#CC293C',
                    border: '1px solid #CC293C',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    height: '32px',
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    opacity: 0.8
                }}>
                    Entry Closed ✕
                </Box>
            );
        }

        if (isUserInThisPool && (howManyEntriesInThisPool >= maxEntriesThisPool || hasJoinDeadlinePassed)) {
            // User in pool, max entries or deadline passed - Joined ✓
            return (
                <Box sx={{
                    backgroundColor: '#22c55e',
                    border: '1px solid #22c55e',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    height: '32px',
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                }}>
                    Joined ✓
                </Box>
            );
        }

        if (isUserInThisPool && howManyEntriesInThisPool < maxEntriesThisPool) {
            // User in pool, can add more entries - Add Entry +
            return (
                <Box
                    onClick={() => joinGameClick()}
                    sx={{
                        backgroundColor: 'transparent',
                        border: '1px solid',
                        borderColor: textColor,
                        borderRadius: '4px',
                        padding: '4px 8px',
                        cursor: 'pointer',
                        fontSize: isMobile ? '10px' : '12px',
                        fontWeight: 600,
                        color: textColor,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        '&:hover': {
                            backgroundColor: 'rgba(255,255,255,0.1)'
                        }
                    }}
                >
                    Add Entry +
                </Box>
            );
        }

        return null;
    };

    return(
        <>
        {/* SNACK BAR */}
            <Box sx={boxStyles}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '12px',
                        alignItems: 'center',
                    }}
                >
                    <Box sx={{display:'flex', flexDirection:'column'}}>
                        <Typography sx={{fontWeight:800}}>{textLineOne}</Typography>
                        <Typography>{textLineTwo}</Typography>
                    </Box>
                    {renderActionButton()}
                </Box>

                <Box sx={{width:'18px'}}>
                    <Ellipses onClick={()=> setMenu(!menu)}/>
                </Box>
            </Box>
        {page === 'home' && (
            <>
                {homePageHeroInfo && (
                     <Box
                        sx={{
                            ...(homePageHeroInfo?.backgroundImage && {
                                backgroundImage: `url(${homePageHeroInfo.backgroundImage})`,
                                backgroundSize: 'cover',
                                backgroundRepeat: 'no-repeat',
                            }),
                            backgroundColor: homePageHeroInfo?.backgroundImage ? 'transparent' : homePageHeroInfo?.backgroundColor || '#ffffff',
                            height: homePageHeroInfo?.overallMaxHeight || '480px',
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            padding: '0 16px',
                            boxSizing: 'border-box',
                        }}
                    >
                        {homePageHeroInfo?.image && (
                            <img
                                style={{
                                    width: '100%',
                                    maxWidth: homePageHeroInfo?.maxWidth || '375px',
                                    height: 'auto',
                                    margin: '0 auto',
                                    display: 'block',
                                    objectFit: 'contain',
                                }}
                                src={homePageHeroInfo.image}
                                alt="hero"
                            />
                        )}
                    </Box>
                )}
            </>
        )}

        {page !== 'home' && otherPagesHeroInfo && (
            <Box
                sx={{
                    ...(otherPagesHeroInfo?.backgroundImage && {
                        backgroundImage: `url(${otherPagesHeroInfo.backgroundImage})`,
                        backgroundSize: 'cover',
                        backgroundRepeat: 'no-repeat',
                    }),
                    backgroundColor: otherPagesHeroInfo?.backgroundImage ? 'transparent' : otherPagesHeroInfo?.backgroundColor || '#ffffff',
                    height: otherPagesHeroInfo?.overallMaxHeight || '180px',
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '0 16px',
                    boxSizing: 'border-box',
                }}
            >
                {otherPagesHeroInfo.image && (
                    <img
                        style={{
                            height: 'auto',
                            width: '100%',
                            maxWidth: otherPagesHeroInfo?.maxWidth || '375px',
                            margin: '0 auto',
                            display: 'block',
                            objectFit: 'contain',
                        }}
                        src={otherPagesHeroInfo.image}
                        alt="hero"
                    />
                )}
            </Box>
        )}

        {menu && (
            <SubMenu menuItems={menuItems} />
        )}

        {/* Content Modals */}
        {activeModal && activeModal !== 'share' && (
            <ContentModal
                open={true}
                onClose={() => setActiveModal(null)}
                activeModal={activeModal}
            />
        )}

        {/* Share Modal */}
        {activeModal === 'share' && (
            <ShareModal
                open={true}
                onClose={() => setActiveModal(null)}
            />
        )}
        </>
    );
}

export default TemplatedSnackbarWithMenu;